import { GuestLiteModel } from 'api-hooks/guest';
import { useGetNotificationByAcl } from 'api-hooks/notification/query';
import {
  DocumentPurchaseLiteModel,
  // GuestLiteModel,
  useGetDocumentPurchases,
} from 'api-hooks/order/travel-document';
import { AuthorizationRules, NavigationRoutes } from 'common/constants';
import { capitalize, string2money } from 'common/utils/string';
import notification from 'components/common/notification';
import QueryFilter from 'components/common/query-filter/query-filter';
import ListHeader from 'components/elements/list-header';
import { IColumn } from 'components/elements/table-component/table-component';
import { useApplyQueryFilter } from 'hooks/filter/use-apply-query-filter';
import { FilterHandleParentProvider } from 'hooks/filter/use-filter-handle-parent';
import useApplyQuerySort from 'hooks/use-apply-query-sort';
import useComposedQuery from 'hooks/use-composed-query';
import useDate from 'hooks/use-date';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useGetListParamsValue from 'hooks/use-get-list-params-value';
import useStatusFilter from 'hooks/use-status-filter';
import { commonColumnTable } from 'legacy-modules/common/column';
import { Container, TopContainer } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';

import TravelDocumentOrderTable from './components/table';
import PurchaseListTabs from '../component/purchase-list-tabs';
import PurchasePillStatus from '../component/status';

export default function TravelDocumentOrderList() {
  const { t } = useTranslation();
  const { queryStatus, queryPage, queryTab } = useGetListParamsValue();
  const [page, setPage] = React.useState(queryPage);
  const [activeKey, setActiveKey] = React.useState<React.Key>(queryTab);
  const [status, setStatus] = useStatusFilter<string | undefined>(
    queryStatus,
    setPage,
  );

  const formatDate = useDate();

  const _columns = React.useMemo<IColumn<DocumentPurchaseLiteModel>[]>(
    () => [
      {
        header: t('common:status'),
        accessorKey: 'status',
        minSize: 250,
        sortName: 'status',
        cell: ({ getValue }) => (
          <PurchasePillStatus value={getValue() as string} />
        ),
      },
      {
        header: t('common:transaction_at'),
        accessorKey: 'transactionAt',
        minSize: 150,
        sortName: 'transaction_at',
        cell: ({ getValue }) => formatDate(getValue() as Date),
      },
      {
        header: t('common:order_number'),
        accessorKey: 'number',
        minSize: 150,
        sortName: 'order_number',
      },
      {
        header: t('common:agent'),
        accessorKey: 'agent',
        minSize: 150,
      },
      {
        header: t('common:guest'),
        accessorKey: 'guests',
        minSize: 150,
        cell: ({ getValue }) => {
          const guests = getValue() as GuestLiteModel[];
          return <>{guests?.map((guest) => guest.name).join(', ')}</>;
        },
      },
      {
        header: t('common:group_code'),
        accessorKey: 'code',
        minSize: 150,
        sortName: 'code',
      },
      {
        header: t('common:description'),
        accessorKey: 'description',
        minSize: 150,
        sortName: 'description',
      },
      {
        header: t('document:document_type'),
        accessorKey: 'documentType',
        minSize: 150,
        cell: ({ row: { original } }) => capitalize(original.documentType),
      },
      {
        header: t('common:total_order'),
        accessorKey: 'total',
        minSize: 150,
        sortName: 'total',
        textAlign: 'right',
        cell: ({ getValue }) => string2money(getValue() as number),
      },
      ...commonColumnTable(t),
    ],
    [formatDate, t],
  );

  const {
    data,
    refetch,
    isLoading,
    isFetching,
    error,
    query,
    extras: [{ filters, setFilters }, { columns }],
  } = useComposedQuery(
    useGetDocumentPurchases,
    {
      params: {
        page,
        filter: { status },
      },
    },
    useApplyQueryFilter((data: any) => {
      return data.filters;
    }),
    useApplyQuerySort((data: any) => {
      return data.sorts;
    }, _columns),
  );

  const { data: dataNotification, refetch: refetchNotification } =
    useGetNotificationByAcl({
      acl: AuthorizationRules.DocumentPurchasesPage,
    });

  React.useEffect(() => {
    if (!!error && !!error.message) {
      notification.error({ message: error?.message });
    }
  }, [error]);
  const permission = useGetCrudPermission('document-purchases');
  return (
    <FilterHandleParentProvider
      values={{
        filters,
        setFilters,
        page,
        setPage,
      }}
    >
      <Container>
        <ListHeader
          query={query}
          title={t('common:travel_document_purchase')}
          showAdd={permission.create}
          onRefresh={() => {
            refetch();
            refetchNotification();
          }}
          linkAdd={NavigationRoutes.DocumentPurchasesCreate}
        />
        <PurchaseListTabs
          {...{
            activeKey,
            setActiveKey: (value) => {
              setActiveKey(value);
              refetchNotification();
              setFilters();
            },
            setStatus,
            badge: dataNotification?.data,
          }}
        />

        <TopContainer>
          <QueryFilter filters={filters} />
        </TopContainer>
        <TravelDocumentOrderTable
          travelDocument={data?.data || []}
          loading={isLoading || isFetching}
          page={page}
          onPageChange={(page) => setPage(page)}
          meta={data?.meta}
          sortColumns={columns || []}
        />
      </Container>
    </FilterHandleParentProvider>
  );
}
